import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { getErrorSelector, isLoadingCreateLLMPromptSelector, isLoadingUpdateLLMPromptSelector } from '@redux/llmPrompts/llmPromptsSelectors';
import { createLLMPromptThunk, updateLLMPromptThunk } from '@redux/llmPrompts/llmPromptsThunks';
import { LLMPrompt } from '@redux/llmPrompts/llmPromptsTypes';
import { ReduxState } from '@redux/types';
import { Button } from 'components/Button/Button';
import { TextArea } from 'components/TextArea/TextArea';
import { TextField } from 'components/TextField';
import { Text } from 'components/Typography/Text';
import style from './LLMPromptFormDialog.scss';

type PropsFromRedux = ConnectedProps<typeof connectRedux>;

interface OwnProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  prompt?: LLMPrompt | null;
  existingPrompts: LLMPrompt[];
}

type Props = PropsFromRedux & OwnProps;

export { LLMPromptFormDialog };

const LLMPromptFormDialog: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  prompt,
  existingPrompts,
  isLoadingCreate,
  isLoadingUpdate,
  errors,
  createLLMPrompt,
  updateLLMPrompt
}) => {
  const isEditMode = !!prompt;
  const isSaving = isLoadingCreate || isLoadingUpdate;

  const [formData, setFormData] = useState({
    toolName: '',
    toolRuleId: '',
    instruction: '',
    sectionContent: '',
    isActive: true,
    version: '1.0.0'
  });

  const [formErrors, setFormErrors] = useState({
    toolName: '',
    version: ''
  });

  useEffect(() => {
    if (prompt) {
      setFormData({
        toolName: prompt.toolName,
        toolRuleId: prompt.toolRuleId || '',
        instruction: prompt.instruction || '',
        sectionContent: prompt.sectionContent || '',
        isActive: prompt.isActive,
        version: prompt.version
      });
    } else {
      setFormData({
        toolName: '',
        toolRuleId: '',
        instruction: '',
        sectionContent: '',
        isActive: true,
        version: '1.0.0'
      });
    }
    setFormErrors({ toolName: '', version: '' });
  }, [prompt, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors({ ...formErrors, [name]: '' });
    }
  };

  const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, isActive: e.target.checked });
  };

  const validateForm = (): boolean => {
    const errors = {
      toolName: '',
      version: ''
    };
    let isValid = true;

    if (!formData.toolName.trim()) {
      errors.toolName = 'Tool name is required';
      isValid = false;
    } else {
      // Check for duplicate tool name
      const duplicate = existingPrompts.find((p) => p.toolName.toLowerCase() === formData.toolName.trim().toLowerCase() && p.id !== prompt?.id);
      if (duplicate) {
        errors.toolName = 'Tool name already exists';
        isValid = false;
      }
    }

    if (!formData.version.trim()) {
      errors.version = 'Version is required';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    const requestData = {
      toolName: formData.toolName,
      toolRuleId: formData.toolRuleId,
      instruction: formData.instruction,
      sectionContent: formData.sectionContent,
      isActive: formData.isActive,
      version: formData.version
    };

    try {
      if (isEditMode && prompt) {
        await updateLLMPrompt({ ...requestData, id: prompt.id });
      } else {
        await createLLMPrompt(requestData);
      }
      onSuccess();
    } catch (err) {
      console.error('Error saving LLM prompt:', err);
    }
  };

  const saveError = errors['llm-prompts-redux/create-llm-prompt'] || errors['llm-prompts-redux/update-llm-prompt'];

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ className: style.dialogPaper }}>
      <DialogTitle>{isEditMode ? 'Edit LLM Prompt' : 'Add New LLM Prompt'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <div className={style.instructionBox}>
            <Text variant="sm" className={style.instructionText}>
              <strong>Usage Guide:</strong> For game-related prompts (e.g., mind journey, Bible scripture), use the <strong>Instruction</strong> field. For core prompts (e.g.,
              personality, behavior), use <strong>Section Content</strong>.
            </Text>
          </div>
          <div className={style.formContainer}>
            <div className={style.formRow}>
              <TextField
                isMandatory
                name="toolName"
                value={formData.toolName}
                onChange={handleChange}
                label="Tool Name"
                placeholder="Tool Name"
                error={formErrors.toolName}
                disabled={isSaving}
              />
              <TextField name="toolRuleId" value={formData.toolRuleId} onChange={handleChange} label="Tool Rule ID" placeholder="Tool Rule ID" disabled={isSaving} />
            </div>
            <div className={style.formRow}>
              <TextField
                isMandatory
                name="version"
                value={formData.version}
                onChange={handleChange}
                label="Version"
                placeholder="1.0.0"
                error={formErrors.version}
                disabled={isSaving}
              />
            </div>
            <div className={style.checkboxRow}>
              <label className={style.checkboxLabel}>
                <input type="checkbox" checked={formData.isActive} onChange={handleActiveChange} disabled={isSaving} />
                <span>Active</span>
              </label>
            </div>
            <div className={style.formRow}>
              <TextArea
                name="instruction"
                value={formData.instruction}
                onChange={handleChange}
                label="Instruction (Game instructions: mind journey, bible scripture)"
                placeholder="Enter LLM prompt instruction..."
                rows={12}
                disabled={isSaving}
                helperText="This field supports newlines, commas, periods, and quotations"
              />
            </div>
            <div className={style.formRow}>
              <TextArea
                name="sectionContent"
                value={formData.sectionContent}
                onChange={handleChange}
                label="Section Content (Core prompts: Personality, Behavior etc)"
                placeholder="Enter section content..."
                rows={12}
                disabled={isSaving}
                helperText="This field supports newlines, commas, periods, and quotations"
              />
            </div>
            {saveError && (
              <div className={style.errorContainer}>
                <Text variant="sm" className={style.errorText}>
                  Error: {saveError?.message || 'Failed to save prompt'}
                </Text>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={isSaving}>
            {isSaving ? <CircularProgress size={24} /> : isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    isLoadingCreate: isLoadingCreateLLMPromptSelector(state),
    isLoadingUpdate: isLoadingUpdateLLMPromptSelector(state),
    errors: getErrorSelector(state)
  }),
  {
    createLLMPrompt: createLLMPromptThunk,
    updateLLMPrompt: updateLLMPromptThunk
  }
);

export default compose(connectRedux)(LLMPromptFormDialog);
